import LogicFlow from '@logicflow/core';
import '@logicflow/core/dist/style/index.css';

import { registerShapes, SHAPE_DEFAULTS } from './shapes/index';
import { registerEdges } from './edges/index';
import { ContextPad } from './ContextPad';
import { ConnectionRules } from './ConnectionRules';
import { DEFAULT_INPUT_CATALOG } from './inputCatalog';
import { resolveTheme, applyThemeCssVars, DEFAULT_THEME } from './theme';

const DEFAULT_GRID = { visible: true, type: 'mesh', size: 10, thickness: 1 };

const REQ_KIND_TO_EDGE_TYPE = {
  information: 'dmn:InformationRequirement',
  knowledge: 'dmn:KnowledgeRequirement',
  authority: 'dmn:AuthorityRequirement',
};

// ── Palette icons: miniature replicas of each DMN shape ──────────────────────

function buildPaletteIcons(theme) {
  const dec = (theme && theme.decision)              || { fill: '#e6f7ff', stroke: '#1890ff' };
  const inp = (theme && theme.inputData)             || { fill: '#e6fffb', stroke: '#13c2c2' };
  const bkm = (theme && theme.businessKnowledgeModel) || { fill: '#f9f0ff', stroke: '#722ed1' };
  const ks  = (theme && theme.knowledgeSource)       || { fill: '#fffbe6', stroke: '#d48806' };
  const ta  = (theme && theme.textAnnotation)        || { stroke: '#8c8c8c' };
  return {
    decision:        `<svg width="22" height="16" viewBox="0 0 22 16"><rect x="1" y="1" width="20" height="14" fill="${dec.fill}" stroke="${dec.stroke}" stroke-width="2"/></svg>`,
    mlDecision:      `<svg width="22" height="16" viewBox="0 0 22 16"><rect x="1" y="1" width="20" height="14" fill="${dec.fill}" stroke="${dec.stroke}" stroke-width="2"/><rect x="2.5" y="2.5" width="9" height="6" rx="1.2" fill="#fff0f6" stroke="#c41d7f" stroke-width="0.9"/><text x="7" y="7.4" text-anchor="middle" font-size="5" font-weight="700" fill="#c41d7f" font-family="ui-monospace,Menlo,monospace">ML</text></svg>`,
    inputData:       `<svg width="22" height="14" viewBox="0 0 22 14"><rect x="1" y="1" width="20" height="12" rx="6" ry="6" fill="${inp.fill}" stroke="${inp.stroke}" stroke-width="2"/></svg>`,
    bkm:             `<svg width="22" height="14" viewBox="0 0 22 14"><path d="M 4 1 L 21 1 L 21 10 L 18 13 L 1 13 L 1 4 Z" fill="${bkm.fill}" stroke="${bkm.stroke}" stroke-width="2"/></svg>`,
    knowledgeSource: `<svg width="20" height="16" viewBox="0 0 20 16"><path d="M 1 1 L 19 1 L 19 12 Q 15 10 10 12 T 1 12 Z" fill="${ks.fill}" stroke="${ks.stroke}" stroke-width="2"/></svg>`,
    textAnnotation:  `<svg width="22" height="14" viewBox="0 0 22 14"><path d="M 5 1 L 1 1 L 1 13 L 5 13" fill="none" stroke="${ta.stroke}" stroke-width="1.5"/><line x1="4" y1="4" x2="18" y2="4" stroke="${ta.stroke}" stroke-width="0.8"/><line x1="4" y1="7" x2="15" y2="7" stroke="${ta.stroke}" stroke-width="0.8"/><line x1="4" y1="10" x2="11" y2="10" stroke="${ta.stroke}" stroke-width="0.8"/></svg>`,

    // Group-only icons (use currentColor so each header tints them with its accent)
    groupDecisions: `<svg width="22" height="16" viewBox="0 0 22 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="3.5" r="1.8"/><circle cx="5" cy="12.5" r="1.8"/><circle cx="17" cy="12.5" r="1.8"/><line x1="10" y1="5" x2="6" y2="11"/><line x1="12" y1="5" x2="16" y2="11"/></svg>`,
    groupData:      `<svg width="22" height="16" viewBox="0 0 22 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"><ellipse cx="11" cy="3.5" rx="7" ry="2"/><path d="M 4 3.5 L 4 12.5 Q 4 14.5 11 14.5 Q 18 14.5 18 12.5 L 18 3.5"/><path d="M 4 8 Q 4 10 11 10 Q 18 10 18 8" stroke-width="1.1"/></svg>`,
    groupArtifacts: `<svg width="20" height="16" viewBox="0 0 20 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M 13.5 3 L 6 10.5 Q 4 12.5 6 14.5 Q 8 16.5 10 14.5 L 16.5 8 Q 18.5 6 16.5 4 Q 14.5 2 12.5 4 L 7 9.5"/></svg>`,
    groupCatalog:   `<svg width="22" height="14" viewBox="0 0 22 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="3" cy="3" r="1.1" fill="currentColor" stroke="none"/><circle cx="3" cy="7" r="1.1" fill="currentColor" stroke="none"/><circle cx="3" cy="11" r="1.1" fill="currentColor" stroke="none"/><line x1="7" y1="3" x2="19" y2="3"/><line x1="7" y1="7" x2="19" y2="7"/><line x1="7" y1="11" x2="15" y2="11"/></svg>`,
  };
}

const STATIC_ICONS = {
  expand: `<svg width="14" height="14" viewBox="0 0 16 16"><path d="M 2 5 L 8 5 M 2 8 L 8 8 M 2 11 L 8 11 M 10 5 L 14 5 M 10 8 L 14 8 M 10 11 L 14 11" stroke="#666" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  collapse: `<svg width="14" height="14" viewBox="0 0 16 16"><rect x="3" y="3" width="4" height="4" rx="1" fill="#666"/><rect x="9" y="3" width="4" height="4" rx="1" fill="#666"/><rect x="3" y="9" width="4" height="4" rx="1" fill="#666"/><rect x="9" y="9" width="4" height="4" rx="1" fill="#666"/></svg>`,
  search: `<svg width="14" height="14" viewBox="0 0 16 16"><circle cx="7" cy="7" r="5" fill="none" stroke="#999" stroke-width="1.5"/><line x1="11" y1="11" x2="14" y2="14" stroke="#999" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  fitView: `<svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="1.5" fill="none" stroke="#555" stroke-width="1.2"/><path d="M 1 5 L 1 1 L 5 1" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M 11 1 L 15 1 L 15 5" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M 15 11 L 15 15 L 11 15" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M 5 15 L 1 15 L 1 11" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  zoomIn: `<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="7" cy="7" r="5.5" fill="none" stroke="#555" stroke-width="1.3"/><line x1="7" y1="4.5" x2="7" y2="9.5" stroke="#555" stroke-width="1.3" stroke-linecap="round"/><line x1="4.5" y1="7" x2="9.5" y2="7" stroke="#555" stroke-width="1.3" stroke-linecap="round"/><line x1="11" y1="11" x2="14.5" y2="14.5" stroke="#555" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  zoomOut: `<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="7" cy="7" r="5.5" fill="none" stroke="#555" stroke-width="1.3"/><line x1="4.5" y1="7" x2="9.5" y2="7" stroke="#555" stroke-width="1.3" stroke-linecap="round"/><line x1="11" y1="11" x2="14.5" y2="14.5" stroke="#555" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  autoLayout: `<svg width="16" height="16" viewBox="0 0 16 16"><rect x="1" y="2" width="5" height="4" rx="1" fill="none" stroke="#555" stroke-width="1.3"/><rect x="10" y="2" width="5" height="4" rx="1" fill="none" stroke="#555" stroke-width="1.3"/><rect x="1" y="10" width="5" height="4" rx="1" fill="none" stroke="#555" stroke-width="1.3"/><rect x="10" y="10" width="5" height="4" rx="1" fill="none" stroke="#555" stroke-width="1.3"/><path d="M 6 4 L 10 4 M 6 12 L 10 12 M 3.5 6 L 3.5 10 M 12.5 6 L 12.5 10" stroke="#555" stroke-width="1.3" stroke-linecap="round"/></svg>`,
};

// Feature flag — gates the experimental "Decisión ML" preset in the DMN
// palette. Webpack DefinePlugin in sfd-v2 inlines ``process.env.AI_NODES_ENABLED``;
// in non-bundled contexts (tests, standalone usage) it falls back to OFF.
const _AI_NODES_ENABLED = (() => {
  try {
    return typeof process !== 'undefined'
      && process.env
      && process.env.AI_NODES_ENABLED === 'true';
  } catch (_) {
    return false;
  }
})();

function buildPaletteGroups(theme) {
  const dec = (theme && theme.decision)        || { fill: '#e6f7ff', stroke: '#1890ff' };
  const inp = (theme && theme.inputData)       || { fill: '#e6fffb', stroke: '#13c2c2' };
  const ta  = (theme && theme.textAnnotation)  || { stroke: '#8c8c8c' };
  const decisionItems = [
    { type: 'dmn:Decision',               label: 'Decisión',                   icon: 'decision' },
  ];
  if (_AI_NODES_ENABLED) {
    decisionItems.push({ type: 'dmn:Decision', label: 'Decisión ML', icon: 'mlDecision', preset: 'ml' });
  }
  decisionItems.push({ type: 'dmn:BusinessKnowledgeModel', label: 'Business Knowledge Model', icon: 'bkm' });
  return [
    {
      id: 'decisions',
      label: 'Decisiones',
      accent: dec.stroke,
      bg: dec.fill,
      headerIcon: 'groupDecisions',
      items: decisionItems,
    },
    {
      id: 'data',
      label: 'Datos y Conocimiento',
      accent: inp.stroke,
      bg: inp.fill,
      headerIcon: 'groupData',
      items: [
        { type: 'dmn:InputData',      label: 'Input Data',       icon: 'inputData' },
        { type: 'dmn:KnowledgeSource', label: 'Knowledge Source', icon: 'knowledgeSource' },
      ],
    },
    {
      id: 'artifacts',
      label: 'Artefactos',
      accent: ta.stroke,
      bg: (theme && theme.canvas && theme.canvas.background) || '#f5f5f5',
      headerIcon: 'groupArtifacts',
      items: [
        { type: 'dmn:TextAnnotation', label: 'Anotación de texto', icon: 'textAnnotation' },
      ],
    },
  ];
}

function injectPaletteStyles() {
  if (document.getElementById('epx-dmn-palette-styles')) return;
  const style = document.createElement('style');
  style.id = 'epx-dmn-palette-styles';
  style.textContent = `
    .epx-dmn-palette {
      width: 220px;
      background: var(--epx-dmn-panel-bg, #fff);
      color: var(--epx-dmn-panel-text, #0f172a);
      border: 1px solid var(--epx-dmn-panel-border, #e0e0e0);
      border-radius: 8px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.08);
      display: flex;
      flex-direction: column;
      margin: 8px;
      max-height: calc(100% - 16px);
      overflow: hidden;
      transition: width 0.25s ease;
    }
    .epx-dmn-palette.compact { width: 56px; }

    .epx-dmn-palette-topbar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 8px 4px 8px;
      flex-shrink: 0;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-topbar {
      justify-content: center;
      padding: 8px 4px 4px 4px;
    }
    .epx-dmn-palette-search-wrap {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-search-wrap { display: none; }
    .epx-dmn-palette-search-icon {
      position: absolute;
      left: 6px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      display: flex;
      align-items: center;
      color: var(--epx-dmn-panel-text-faint, #999);
    }
    .epx-dmn-palette-search-icon svg circle,
    .epx-dmn-palette-search-icon svg line { stroke: currentColor; }
    .epx-dmn-palette-search {
      width: 100%;
      height: 28px;
      border: 1px solid var(--epx-dmn-panel-border, #e0e0e0);
      border-radius: 6px;
      padding: 0 8px 0 24px;
      font-size: 11px;
      outline: none;
      background: var(--epx-dmn-panel-hover, #fafafa);
      color: var(--epx-dmn-panel-text, #333);
      font-family: inherit;
    }
    .epx-dmn-palette-search::placeholder {
      color: var(--epx-dmn-panel-text-faint, #999);
    }
    .epx-dmn-palette-search:focus {
      border-color: var(--epx-dmn-accent, #1976d2);
      background: var(--epx-dmn-panel-bg, #fff);
      box-shadow: 0 0 0 2px var(--epx-dmn-accent-alpha, rgba(25,118,210,0.1));
    }
    .epx-dmn-palette-toggle-btn {
      width: 28px; height: 28px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid var(--epx-dmn-panel-border, #e0e0e0);
      border-radius: 6px;
      background: var(--epx-dmn-panel-hover, #fafafa);
      color: var(--epx-dmn-panel-text-muted, #555);
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.15s, border-color 0.15s;
    }
    .epx-dmn-palette-toggle-btn:hover {
      background: var(--epx-dmn-accent-bg, #e3f2fd);
      border-color: var(--epx-dmn-accent-border, #90caf9);
    }
    .epx-dmn-palette-toggle-btn svg path { stroke: currentColor; fill: none; stroke-width: 1.5; }
    .epx-dmn-palette-toggle-btn svg rect { fill: currentColor; }

    .epx-dmn-palette-tools {
      display: flex;
      justify-content: center;
      gap: 2px;
      padding: 4px 8px 6px 8px;
      flex-shrink: 0;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-tools {
      flex-wrap: wrap;
      padding: 4px 4px 6px 4px;
    }
    .epx-dmn-palette-tool-btn {
      width: 28px; height: 28px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid transparent;
      border-radius: 6px;
      background: none;
      color: var(--epx-dmn-panel-text-muted, #555);
      cursor: pointer;
      padding: 2px;
      transition: all 0.15s;
    }
    .epx-dmn-palette-tool-btn:hover {
      background: var(--epx-dmn-panel-hover, #f0f0f0);
      border-color: var(--epx-dmn-panel-border, #d9d9d9);
    }
    .epx-dmn-palette-tool-btn svg path,
    .epx-dmn-palette-tool-btn svg circle,
    .epx-dmn-palette-tool-btn svg rect,
    .epx-dmn-palette-tool-btn svg line { stroke: currentColor; }

    .epx-dmn-palette-divider {
      height: 0;
      margin: 2px 10px;
      border-top: 1px solid var(--epx-dmn-panel-border, #f0f0f0);
      flex-shrink: 0;
    }

    .epx-dmn-palette-groups {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding-bottom: 8px;
    }
    .epx-dmn-palette-groups::-webkit-scrollbar { width: 4px; }
    .epx-dmn-palette-groups::-webkit-scrollbar-track { background: transparent; }
    .epx-dmn-palette-groups::-webkit-scrollbar-thumb {
      background: var(--epx-dmn-panel-border, #d9d9d9);
      border-radius: 2px;
    }

    .epx-dmn-palette-group {
      margin: 2px 6px;
      border-radius: 6px;
      overflow: hidden;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-group { margin: 2px 3px; }

    .epx-dmn-palette-group-header {
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 6px;
      transition: all 0.15s;
      border: 1px solid transparent;
    }
    .epx-dmn-palette-group-header:hover { opacity: 0.85; }
    .epx-dmn-palette-group-header.expanded { border-radius: 6px 6px 0 0; }
    .epx-dmn-palette.compact .epx-dmn-palette-group-header {
      justify-content: center;
      padding: 8px 4px;
      gap: 0;
    }
    .epx-dmn-palette-group-header-icon {
      width: 22px; height: 20px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .epx-dmn-palette-group-header-label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 10px;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-group-header-label { display: none; }
    .epx-dmn-palette-group-count {
      font-size: 9px;
      font-weight: 500;
      color: var(--epx-dmn-panel-text-muted, #999);
      background: var(--epx-dmn-panel-hover, #f0f0f0);
      border-radius: 8px;
      padding: 1px 5px;
      flex-shrink: 0;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-group-count { display: none; }
    .epx-dmn-palette-group-chevron {
      font-size: 10px;
      transition: transform 0.2s ease;
      color: var(--epx-dmn-panel-text-faint, #999);
      flex-shrink: 0;
    }
    .epx-dmn-palette-group-chevron.collapsed { transform: rotate(-90deg); }
    .epx-dmn-palette.compact .epx-dmn-palette-group-chevron { display: none; }

    .epx-dmn-palette-group-body {
      max-height: 500px;
      overflow: hidden;
      transition: max-height 0.25s ease, opacity 0.2s ease;
      opacity: 1;
      border-radius: 0 0 6px 6px;
    }
    .epx-dmn-palette-group-body.collapsed { max-height: 0; opacity: 0; }

    .epx-dmn-palette-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      cursor: grab;
      transition: background 0.12s;
      user-select: none;
      -webkit-user-select: none;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-item {
      justify-content: center;
      padding: 6px 4px;
      gap: 0;
    }
    .epx-dmn-palette-item:hover { background: var(--epx-dmn-accent-bg, rgba(25,118,210,0.06)); }
    .epx-dmn-palette-item:active {
      cursor: grabbing;
      background: var(--epx-dmn-accent-alpha, rgba(25,118,210,0.12));
    }
    .epx-dmn-palette-item.filtered-out { display: none; }
    .epx-dmn-palette-icon {
      width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .epx-dmn-palette-item-label {
      font-size: 11.5px;
      color: var(--epx-dmn-panel-text, #444);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-item-label { display: none; }

    .epx-dmn-palette-no-results {
      padding: 16px 10px;
      text-align: center;
      color: var(--epx-dmn-panel-text-faint, #bbb);
      font-size: 11px;
      font-style: italic;
    }

    .epx-dmn-palette-item-badge {
      font-size: 9px;
      font-weight: 500;
      color: var(--epx-dmn-panel-text-muted, #64748b);
      background: var(--epx-dmn-panel-hover, #f0f0f0);
      border-radius: 8px;
      padding: 1px 6px;
      flex-shrink: 0;
      font-family: "Menlo", "Consolas", "Courier New", monospace;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-item-badge { display: none; }

    .epx-dmn-palette-section-divider {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 14px 12px 8px 12px;
      flex-shrink: 0;
    }
    .epx-dmn-palette-section-divider::before,
    .epx-dmn-palette-section-divider::after {
      content: "";
      flex: 1;
      border-top: 1px solid var(--epx-dmn-panel-border, #c7d2fe);
    }
    .epx-dmn-palette-section-divider-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      color: var(--epx-dmn-accent, #6366f1);
      white-space: nowrap;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-section-divider {
      margin: 10px 4px 6px 4px;
    }
    .epx-dmn-palette.compact .epx-dmn-palette-section-divider-label { display: none; }
  `;
  document.head.appendChild(style);
}

export class DrdEditor {
  constructor(container, options = {}) {
    const containerEl = typeof container === 'string'
      ? document.querySelector(container)
      : container;

    if (!containerEl) throw new Error('[DrdEditor] container not found');

    this._containerEl = containerEl;
    this._options = options;
    this._eventHandlers = options.eventHandlers || {};
    this._inputCatalog = Array.isArray(options.inputCatalog)
      ? options.inputCatalog
      : DEFAULT_INPUT_CATALOG;
    this._collapsedGroups = new Set(['catalog']);
    this._isCompact = false;
    this._searchTerm = '';
    this._itemEls = [];

    this._theme = resolveTheme(options.theme);
    this._gridOptions = { ...DEFAULT_GRID, ...(options.grid || {}) };

    this._buildLayout();

    // Always pass a grid config to LogicFlow so the `.lf-grid` SVG element
    // exists in the DOM. If the host wants the grid hidden, we toggle display
    // after mount — otherwise turning it back on later wouldn't work.
    const lfGrid = {
      size: this._gridOptions.size || 10,
      visible: true,
      type: this._gridOptions.type || 'mesh',
      config: {
        color: this._gridOptions.color || (this._theme.grid && this._theme.grid.color) || DEFAULT_THEME.grid.color,
        thickness: this._gridOptions.thickness || 1,
      },
    };

    this._lf = new LogicFlow({
      container: this._canvasEl,
      grid: lfGrid,
      keyboard: {
        enabled: true,
        shortcuts: [
          {
            keys: ['backspace', 'delete'],
            callback: () => {
              const { nodes = [], edges = [] } = this._lf.getSelectElements(true) || {};
              edges.forEach((e) => { try { this._lf.deleteEdge(e.id); } catch (_) {} });
              nodes.forEach((n) => { try { this._lf.deleteNode(n.id); } catch (_) {} });
            },
          },
        ],
      },
      edgeType: 'dmn:InformationRequirement',
      isSilentMode: false,
    });

    registerShapes(this._lf);
    registerEdges(this._lf);

    // Stash the resolved theme on graphModel so SVG shape views can read it
    // via getThemeStyle(graphModel, key).
    if (this._lf.graphModel) {
      this._lf.graphModel.__epxDmnTheme = this._theme;
    }
    this._patchLfTextTheme();
    this._applyThemeToDom();

    // Honor initial visibility — LF was given a grid config unconditionally so
    // the SVG element exists, but if the host asked for grid:false we hide it.
    // Hosts (sfd-v2) often build the editor while the surrounding overlay is
    // still `display:none`, in which case LogicFlow may not have rendered the
    // .lf-grid SVG yet — defer with rAF and a MutationObserver to catch the
    // late mount instead of leaving the grid visible.
    this._applyInitialGridVisibility();

    this._connRules = new ConnectionRules(this._lf);
    this._contextPad = new ContextPad(this._lf, this._connRules, this._eventHandlers);

    this._buildPalette();
    this._bindEvents();
  }

  _buildLayout() {
    this._containerEl.innerHTML = '';
    this._containerEl.style.display = 'flex';
    this._containerEl.style.flexDirection = 'row';
    this._containerEl.style.width = '100%';
    this._containerEl.style.height = '100%';
    const bg = (this._theme && this._theme.canvas && this._theme.canvas.background) || '#f5f7fa';
    this._containerEl.style.background = bg;

    this._paletteEl = document.createElement('div');
    this._paletteEl.className = 'epx-dmn-palette';

    this._canvasEl = document.createElement('div');
    this._canvasEl.className = 'epx-dmn-canvas';
    this._canvasEl.style.flex = '1';
    this._canvasEl.style.minWidth = '0';
    this._canvasEl.style.position = 'relative';

    this._containerEl.appendChild(this._paletteEl);
    this._containerEl.appendChild(this._canvasEl);

    injectPaletteStyles();
  }

  _buildPalette() {
    this._paletteEl.innerHTML = '';

    // Top bar: search + compact toggle
    const topbar = document.createElement('div');
    topbar.className = 'epx-dmn-palette-topbar';

    const searchWrap = document.createElement('div');
    searchWrap.className = 'epx-dmn-palette-search-wrap';
    const searchIcon = document.createElement('span');
    searchIcon.className = 'epx-dmn-palette-search-icon';
    searchIcon.innerHTML = STATIC_ICONS.search;
    searchWrap.appendChild(searchIcon);
    const searchInput = document.createElement('input');
    searchInput.className = 'epx-dmn-palette-search';
    searchInput.type = 'text';
    searchInput.placeholder = 'Buscar…';
    searchInput.addEventListener('input', (e) => {
      this._searchTerm = e.target.value.toLowerCase().trim();
      this._applyFilter();
    });
    searchWrap.appendChild(searchInput);
    topbar.appendChild(searchWrap);

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'epx-dmn-palette-toggle-btn';
    toggleBtn.type = 'button';
    toggleBtn.innerHTML = STATIC_ICONS.collapse;
    toggleBtn.title = 'Alternar vista';
    toggleBtn.addEventListener('click', () => {
      this._isCompact = !this._isCompact;
      this._paletteEl.classList.toggle('compact', this._isCompact);
      toggleBtn.innerHTML = this._isCompact ? STATIC_ICONS.expand : STATIC_ICONS.collapse;
    });
    topbar.appendChild(toggleBtn);

    this._paletteEl.appendChild(topbar);

    // Tools: zoom, fit
    const tools = document.createElement('div');
    tools.className = 'epx-dmn-palette-tools';
    const mkTool = (icon, title, handler) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'epx-dmn-palette-tool-btn';
      b.title = title;
      b.innerHTML = STATIC_ICONS[icon];
      b.addEventListener('click', (e) => { e.stopPropagation(); handler(); });
      return b;
    };
    tools.appendChild(mkTool('zoomIn', 'Acercar', () => this._lf && this._lf.zoom(true)));
    tools.appendChild(mkTool('zoomOut', 'Alejar', () => this._lf && this._lf.zoom(false)));
    tools.appendChild(mkTool('fitView', 'Ajustar vista', () => this.fit()));
    tools.appendChild(mkTool('autoLayout', 'Organizar diagrama', () => {
      this.autoLayout().catch((err) => console.error('[epx-dmn] autoLayout error:', err));
    }));
    this._paletteEl.appendChild(tools);

    const divider = document.createElement('div');
    divider.className = 'epx-dmn-palette-divider';
    this._paletteEl.appendChild(divider);

    // Groups
    const groups = document.createElement('div');
    groups.className = 'epx-dmn-palette-groups';

    this._itemEls = [];
    this._paletteIcons = buildPaletteIcons(this._theme);
    for (const group of buildPaletteGroups(this._theme)) {
      groups.appendChild(this._buildGroup(group));
    }

    if (this._inputCatalog && this._inputCatalog.length) {
      const sectionDivider = document.createElement('div');
      sectionDivider.className = 'epx-dmn-palette-section-divider';
      const sectionLabel = document.createElement('span');
      sectionLabel.className = 'epx-dmn-palette-section-divider-label';
      sectionLabel.textContent = 'desde servidor';
      sectionDivider.appendChild(sectionLabel);
      groups.appendChild(sectionDivider);

      groups.appendChild(this._buildCatalogGroup(this._inputCatalog));
    }

    this._noResultsEl = document.createElement('div');
    this._noResultsEl.className = 'epx-dmn-palette-no-results';
    this._noResultsEl.textContent = 'Sin resultados';
    this._noResultsEl.style.display = 'none';
    groups.appendChild(this._noResultsEl);

    this._paletteEl.appendChild(groups);
  }

  _buildGroup(group) {
    const groupEl = document.createElement('div');
    groupEl.className = 'epx-dmn-palette-group';
    groupEl.setAttribute('data-group', group.id);

    const header = document.createElement('div');
    header.className = 'epx-dmn-palette-group-header expanded';
    header.style.background = group.bg;
    header.style.borderColor = group.accent + '66';
    header.style.color = group.accent;

    const hIcon = document.createElement('span');
    hIcon.className = 'epx-dmn-palette-group-header-icon';
    hIcon.innerHTML = this._paletteIcons[group.headerIcon] || '';
    header.appendChild(hIcon);

    const label = document.createElement('span');
    label.className = 'epx-dmn-palette-group-header-label';
    label.textContent = group.label;
    header.appendChild(label);

    const count = document.createElement('span');
    count.className = 'epx-dmn-palette-group-count';
    count.textContent = group.items.length;
    header.appendChild(count);

    const chevron = document.createElement('span');
    chevron.className = 'epx-dmn-palette-group-chevron';
    chevron.textContent = '▾';
    header.appendChild(chevron);

    const body = document.createElement('div');
    body.className = 'epx-dmn-palette-group-body';
    body.style.background = group.bg + '80';

    for (const item of group.items) {
      body.appendChild(this._buildPaletteItem(item));
    }

    header.addEventListener('click', () => {
      const isCollapsed = this._collapsedGroups.has(group.id);
      if (isCollapsed) {
        this._collapsedGroups.delete(group.id);
        body.classList.remove('collapsed');
        chevron.classList.remove('collapsed');
        header.classList.add('expanded');
      } else {
        this._collapsedGroups.add(group.id);
        body.classList.add('collapsed');
        chevron.classList.add('collapsed');
        header.classList.remove('expanded');
      }
    });

    groupEl.appendChild(header);
    groupEl.appendChild(body);
    return groupEl;
  }

  _buildCatalogGroup(catalog) {
    // Catálogo agrupa InputData server-side, así que usamos los mismos tonos
    // que el bloque "Datos y Conocimiento" para que el chip respete el tema
    // activo (antes estaba codificado a un indigo que se veía mal en oscuro).
    const inp = (this._theme && this._theme.inputData) || { fill: '#eef2ff', stroke: '#6366f1' };
    const group = {
      id: 'catalog',
      label: 'Catálogo de Inputs',
      accent: inp.stroke,
      bg: inp.fill,
      headerIcon: 'groupCatalog',
    };

    const isCollapsed = this._collapsedGroups.has(group.id);

    const groupEl = document.createElement('div');
    groupEl.className = 'epx-dmn-palette-group';
    groupEl.setAttribute('data-group', group.id);

    const header = document.createElement('div');
    header.className = 'epx-dmn-palette-group-header' + (isCollapsed ? '' : ' expanded');
    header.style.background = group.bg;
    header.style.borderColor = group.accent + '66';
    header.style.color = group.accent;

    const hIcon = document.createElement('span');
    hIcon.className = 'epx-dmn-palette-group-header-icon';
    hIcon.innerHTML = this._paletteIcons[group.headerIcon] || '';
    header.appendChild(hIcon);

    const label = document.createElement('span');
    label.className = 'epx-dmn-palette-group-header-label';
    label.textContent = group.label;
    header.appendChild(label);

    const count = document.createElement('span');
    count.className = 'epx-dmn-palette-group-count';
    count.textContent = catalog.length;
    header.appendChild(count);

    const chevron = document.createElement('span');
    chevron.className = 'epx-dmn-palette-group-chevron' + (isCollapsed ? ' collapsed' : '');
    chevron.textContent = '▾';
    header.appendChild(chevron);

    const body = document.createElement('div');
    body.className = 'epx-dmn-palette-group-body' + (isCollapsed ? ' collapsed' : '');
    body.style.background = group.bg + '80';

    catalog.forEach((entry) => body.appendChild(this._buildCatalogItem(entry)));

    header.addEventListener('click', () => {
      const isCollapsed = this._collapsedGroups.has(group.id);
      if (isCollapsed) {
        this._collapsedGroups.delete(group.id);
        body.classList.remove('collapsed');
        chevron.classList.remove('collapsed');
        header.classList.add('expanded');
      } else {
        this._collapsedGroups.add(group.id);
        body.classList.add('collapsed');
        chevron.classList.add('collapsed');
        header.classList.remove('expanded');
      }
    });

    groupEl.appendChild(header);
    groupEl.appendChild(body);
    return groupEl;
  }

  _buildCatalogItem(entry) {
    const el = document.createElement('div');
    el.className = 'epx-dmn-palette-item';
    const searchText = `${entry.label || ''} ${entry.name || ''} ${entry.category || ''}`.toLowerCase();
    const tooltipLines = [
      entry.label || entry.name,
      entry.name ? `FEEL: ${entry.name} : ${entry.typeRef || 'any'}` : null,
      entry.description || null,
    ].filter(Boolean);
    el.setAttribute('title', tooltipLines.join('\n'));
    el.setAttribute('data-search', searchText);

    const iconWrap = document.createElement('div');
    iconWrap.className = 'epx-dmn-palette-icon';
    iconWrap.innerHTML = this._paletteIcons.inputData;
    el.appendChild(iconWrap);

    const labelEl = document.createElement('span');
    labelEl.className = 'epx-dmn-palette-item-label';
    labelEl.textContent = entry.label || entry.name || entry.key;
    el.appendChild(labelEl);

    if (entry.typeRef) {
      const badge = document.createElement('span');
      badge.className = 'epx-dmn-palette-item-badge';
      badge.textContent = entry.typeRef;
      el.appendChild(badge);
    }

    this._itemEls.push({ el, search: searchText });

    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const defaults = SHAPE_DEFAULTS['dmn:InputData'] || { width: 140, height: 48 };
      this._lf.dnd.startDrag({
        type: 'dmn:InputData',
        properties: {
          name: entry.name,
          width: defaults.width,
          height: defaults.height,
          catalogKey: entry.key,
          catalogTypeRef: entry.typeRef,
          catalogLabel: entry.label,
        },
        text: entry.name,
      });
    });

    return el;
  }

  _buildPaletteItem(item) {
    const el = document.createElement('div');
    el.className = 'epx-dmn-palette-item';
    el.setAttribute('title', item.label);
    el.setAttribute('data-search', item.label.toLowerCase());

    const iconWrap = document.createElement('div');
    iconWrap.className = 'epx-dmn-palette-icon';
    iconWrap.innerHTML = this._paletteIcons[item.icon] || '';
    el.appendChild(iconWrap);

    const labelEl = document.createElement('span');
    labelEl.className = 'epx-dmn-palette-item-label';
    labelEl.textContent = item.label;
    el.appendChild(labelEl);

    this._itemEls.push({ el, search: item.label.toLowerCase() });

    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const defaults = SHAPE_DEFAULTS[item.type] || { width: 140, height: 60 };
      const properties = {
        name: item.label,
        width: defaults.width,
        height: defaults.height,
      };
      if (item.preset) {
        properties.preset = item.preset;
        if (item.preset === 'ml') properties.logicType = 'epx:MachineLearningDecision';
      }
      this._lf.dnd.startDrag({
        type: item.type,
        properties,
        text: item.label,
      });
    });

    return el;
  }

  _applyFilter() {
    const term = this._searchTerm;
    let visible = 0;
    for (const { el, search } of this._itemEls) {
      const match = !term || search.includes(term);
      el.classList.toggle('filtered-out', !match);
      if (match) visible += 1;
    }
    if (this._noResultsEl) {
      this._noResultsEl.style.display = visible === 0 ? 'block' : 'none';
    }
  }

  _bindEvents() {
    this._lf.on('node:click', ({ data }) => {
      this._emit('element.click', { element: data });
      this._emit('selection.changed', { newSelection: data ? [data] : [] });
    });
    this._lf.on('node:dbclick', ({ data }) => {
      if (data && (data.type === 'dmn:Decision' || data.type === 'dmn:BusinessKnowledgeModel')) {
        this._emit('node.inlineEdit', { element: data });
      }
    });
    this._lf.on('edge:click', ({ data }) => {
      this._emit('element.click', { element: data });
      this._emit('selection.changed', { newSelection: data ? [data] : [] });
    });
    this._lf.on('blank:click', () => {
      this._emit('element.click', { element: null });
      this._emit('selection.changed', { newSelection: [] });
    });
    // SelectionSelect (lasso) — emit only selection.changed (no element.click,
    // which would otherwise overwrite a multi-selection with a single element).
    this._lf.on('selection:selected', (elements) => {
      this._emit('selection.changed', { newSelection: elements || [] });
    });

    this._lf.on('node:add', ({ data }) => this._emit('element.added', { element: data }));
    this._lf.on('node:dnd-add', ({ data }) => this._emit('element.added', { element: data }));
    this._lf.on('node:delete', ({ data }) => this._emit('element.removed', { element: data }));
    this._lf.on('node:drop', ({ data }) => this._emit('element.moved', { element: data }));
    this._lf.on('text:update', (payload) => {
      // LogicFlow emits text:update with a flat payload { id, text, type },
      // not { data }. Also, shape views render the label from properties.name,
      // but the inline editor only writes to text.value — sync both so the
      // label re-renders and drg.name stays in sync with what the user typed.
      if (!payload || !payload.id) return;
      const newValue = payload.text || '';
      const m = this._lf.getNodeModelById(payload.id);
      if (m && typeof m.setProperties === 'function') {
        m.setProperties({ ...(m.properties || {}), name: newValue });
      }
      const element = {
        id: payload.id,
        type: payload.type,
        text: { value: newValue },
        properties: m ? { ...(m.properties || {}) } : { name: newValue },
      };
      this._emit('element.renamed', { element });
    });

    this._lf.on('edge:add', ({ data }) => {
      if (data?.type === 'dmn:KnowledgeRequirement') {
        this._refreshBkmBadge(data.targetNodeId);
      }
      this._emit('edge.added', { edge: data });
    });
    this._lf.on('edge:delete', ({ data }) => {
      if (data?.type === 'dmn:KnowledgeRequirement') {
        this._refreshBkmBadge(data.targetNodeId);
      }
      this._emit('edge.removed', { edge: data });
    });
  }

  _refreshBkmBadge(decisionId) {
    if (!decisionId || !this._lf) return;
    const m = this._lf.getNodeModelById(decisionId);
    if (!m || m.type !== 'dmn:Decision') return;
    const { edges } = this._lf.getGraphData();
    const hasBkm = edges.some(
      (e) => e.type === 'dmn:KnowledgeRequirement' && e.targetNodeId === decisionId,
    );
    if ((m.properties?.hasBkmConnected || false) !== hasBkm) {
      m.setProperties({ ...(m.properties || {}), hasBkmConnected: hasBkm });
    }
  }

  _emit(event, payload) {
    const handlers = this._eventHandlers[event];
    if (!handlers) return;
    handlers.forEach((h) => { try { h(payload); } catch (e) { console.error(e); } });
  }

  _applyThemeToDom() {
    if (!this._theme) return;
    applyThemeCssVars(this._containerEl, this._theme);
    const bg = this._theme.canvas && this._theme.canvas.background;
    if (bg) {
      this._containerEl.style.background = bg;
      if (this._canvasEl) {
        this._canvasEl.style.background = bg;
        // LogicFlow's stylesheet hardcodes `.lf-graph { background: #fff }`
        // so the host bg never shows through. Override inline (same trick
        // epx-flow uses).
        const lfGraph = this._canvasEl.querySelector('.lf-graph');
        if (lfGraph) lfGraph.style.background = bg;
      }
    }
    const txt = this._theme.text && this._theme.text.fill;
    if (txt) this._containerEl.style.color = txt;
  }

  /**
   * Patch LogicFlow's internal graphModel.theme so that node/edge text uses
   * the current theme's text color and edge labels don't render with a white
   * SVG rect background. Mirrors epx-flow's _patchLfTextTheme.
   */
  _patchLfTextTheme() {
    const lft = this._lf && this._lf.graphModel && this._lf.graphModel.theme;
    if (!lft) return;
    const textFill = (this._theme && this._theme.text && this._theme.text.fill) || '#333';
    if (lft.nodeText) lft.nodeText.color = textFill;
    if (lft.edgeText) {
      lft.edgeText.color = textFill;
      if (lft.edgeText.background) lft.edgeText.background.fill = 'transparent';
    }
  }

  /**
   * Apply the configured grid visibility against `.lf-grid`. Tolerates the
   * SVG not being in the DOM yet (LogicFlow renders it lazily when the host
   * container becomes visible) by retrying on rAF and via a one-shot
   * MutationObserver.
   */
  _applyInitialGridVisibility() {
    if (!this._canvasEl) return;
    const visible = this._gridOptions.visible !== false;
    const apply = () => {
      const gridEl = this._canvasEl && this._canvasEl.querySelector('.lf-grid');
      if (!gridEl) return false;
      gridEl.style.display = visible ? '' : 'none';
      if (this._lf && this._lf.graphModel) {
        this._lf.graphModel.gridSize = visible ? (this._gridOptions.size || 10) : 1;
      }
      return true;
    };
    if (apply()) return;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => { if (apply()) return; });
    }
    if (typeof MutationObserver === 'function') {
      const obs = new MutationObserver(() => {
        if (apply()) { obs.disconnect(); this._gridObserver = null; }
      });
      try { obs.observe(this._canvasEl, { childList: true, subtree: true }); } catch (_) { /* noop */ }
      this._gridObserver = obs;
    }
  }

  /**
   * Rebuild the SVG <pattern> child for the current grid type/size/color.
   * Mirrors epx-flow's _applyGridPattern — owning the pattern is more reliable
   * than mutating LF's defaults because LF re-emits the pattern on each render.
   */
  _applyGridPattern(gridEl) {
    const svgNS   = 'http://www.w3.org/2000/svg';
    const pattern = gridEl && gridEl.querySelector('pattern');
    if (!pattern) return;

    const opts      = this._gridOptions || {};
    const size      = opts.size || 20;
    const color     = opts.color || (this._theme && this._theme.grid && this._theme.grid.color) || DEFAULT_THEME.grid.color;
    const thickness = opts.thickness || 1;
    const type      = opts.type || 'mesh';

    Array.from(pattern.children).forEach((el) => el.remove());
    pattern.setAttribute('width',  size);
    pattern.setAttribute('height', size);

    if (type === 'dot') {
      const dotSize = Math.max(1, Math.min(thickness * 2, size / 4));
      const rect = document.createElementNS(svgNS, 'rect');
      rect.setAttribute('width',  dotSize);
      rect.setAttribute('height', dotSize);
      rect.setAttribute('rx', dotSize / 2);
      rect.setAttribute('ry', dotSize / 2);
      rect.setAttribute('fill', color);
      pattern.appendChild(rect);
    } else {
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d',            `M ${size} 0 H0 M0 0 V0 ${size}`);
      path.setAttribute('fill',         'none');
      path.setAttribute('stroke',       color);
      path.setAttribute('stroke-width', thickness);
      pattern.appendChild(path);
    }
  }

  /**
   * Apply a new theme. Mutates the in-place theme object so SVG shapes (which
   * read from graphModel.__epxDmnTheme via getThemeStyle) pick up the change
   * on the next render() pass. Also retints the live grid pattern, since
   * LogicFlow does not refresh the grid stroke from theme.
   */
  setTheme(theme) {
    this._theme = resolveTheme(theme);
    if (this._lf && this._lf.graphModel) {
      this._lf.graphModel.__epxDmnTheme = this._theme;
    }
    this._applyThemeToDom();

    // Sync grid color with the new theme — clear any cached override so the
    // pattern picks up theme.grid.color, then rebuild the pattern element.
    this._gridOptions = { ...(this._gridOptions || {}), color: undefined };
    if (this._canvasEl) {
      const gridEl = this._canvasEl.querySelector('.lf-grid');
      if (gridEl) this._applyGridPattern(gridEl);
    }

    // Patch LF's internal text theme so labels use the new text color.
    this._patchLfTextTheme();

    // Rebuild the palette so its icons (DRG node thumbnails) and group
    // accents/backgrounds reflect the new theme.
    if (this._paletteEl) this._buildPalette();

    // Force every node/edge view to re-evaluate getNodeStyle/getEdgeStyle.
    // __epxDmnTheme isn't MobX-observable; bumping a counter property on each
    // model IS reactive, and getNodeStyle reads the updated graphModel theme
    // when MobX re-runs the view's render.
    if (this._lf && this._lf.graphModel) {
      this._themeVersion = (this._themeVersion || 0) + 1;
      const tv = this._themeVersion;
      const gm = this._lf.graphModel;
      try {
        (gm.nodes || []).forEach((n) => {
          if (typeof n.setProperty === 'function') n.setProperty('__tv', tv);
        });
        (gm.edges || []).forEach((e) => {
          if (typeof e.setProperty === 'function') e.setProperty('__tv', tv);
        });
      } catch (_) { /* noop */ }
    }
  }

  /**
   * Live grid update — owns its own pattern (mirrors epx-flow), so visibility,
   * color, type and size all update in place without a remount.
   */
  setGrid(opts = {}) {
    this._gridOptions = { ...(this._gridOptions || {}), ...opts };
    const gridEl = this._canvasEl && this._canvasEl.querySelector('.lf-grid');
    if (!gridEl) return true;

    this._applyGridPattern(gridEl);

    const visible = this._gridOptions.visible !== false;
    gridEl.style.display = visible ? '' : 'none';
    if (this._lf && this._lf.graphModel) {
      this._lf.graphModel.gridSize = visible ? (this._gridOptions.size || 10) : 1;
    }
    return true;
  }

  setGridVisible(visible) {
    return this.setGrid({ visible: !!visible });
  }

  getGridOptions() {
    return { ...this._gridOptions };
  }

  render(dmnModel) {
    if (!dmnModel) {
      this._lf.render({ nodes: [], edges: [] });
      return;
    }

    const drgById = new Map();
    const reqById = new Map();

    dmnModel.drgElements.forEach((el) => {
      drgById.set(el.id, el);
      el.requirements.forEach((req) => {
        if (req.id) reqById.set(req.id, req);
      });
    });

    const nodes = [];
    const edges = [];
    const diagram = dmnModel.diagram;

    if (diagram) {
      diagram.shapes.forEach((shape) => {
        const drg = drgById.get(shape.dmnElementRef);
        if (!drg) return;
        const defaults = SHAPE_DEFAULTS[drg.type] || { width: 120, height: 60 };
        const b = shape.bounds;
        const width = b.width || defaults.width;
        const height = b.height || defaults.height;
        nodes.push({
          id: drg.id,
          type: drg.type,
          x: (b.x || 0) + width / 2,
          y: (b.y || 0) + height / 2,
          properties: {
            name: drg.name,
            dmnElementRef: drg.id,
            logicType: drg.decisionLogic?.type || (drg.encapsulatedLogic ? drg.encapsulatedLogic.type : null),
          },
          text: drg.name ? { value: drg.name, x: (b.x || 0) + width / 2, y: (b.y || 0) + height / 2 } : undefined,
        });
      });

      diagram.edges.forEach((edge) => {
        const req = reqById.get(edge.dmnElementRef);
        if (!req) return;
        const edgeType = REQ_KIND_TO_EDGE_TYPE[req.kind] || 'dmn:InformationRequirement';
        const wp = edge.waypoints && edge.waypoints.length ? edge.waypoints : null;
        edges.push({
          id: edge.id,
          type: edgeType,
          sourceNodeId: req.sourceRef,
          targetNodeId: req.targetRef,
          pointsList: wp,
          startPoint: wp ? wp[0] : undefined,
          endPoint: wp ? wp[wp.length - 1] : undefined,
          properties: { dmnElementRef: req.id },
        });
      });
    }

    const decisionsWithBkm = new Set();
    edges.forEach((e) => {
      if (e.type === 'dmn:KnowledgeRequirement') decisionsWithBkm.add(e.targetNodeId);
    });
    nodes.forEach((n) => {
      if (n.type === 'dmn:Decision') {
        n.properties.hasBkmConnected = decisionsWithBkm.has(n.id);
      }
    });

    this._lf.render({ nodes, edges });
  }

  fit() {
    if (typeof this._lf.fitView === 'function') {
      this._lf.fitView();
    }
  }

  /** Accessor for callers that need to mutate node properties directly. */
  getLogicFlow() {
    return this._lf;
  }

  /** Toggle a simulation-status property on a node. Safe no-op if missing.
   *  LogicFlow.setProperties merges, so to "clear" we write null explicitly —
   *  the shape helper treats falsy values as "no sim status, draw default". */
  setNodeSimStatus(nodeId, status) {
    if (!this._lf || !nodeId) return;
    const m = this._lf.getNodeModelById(nodeId);
    if (!m || typeof m.setProperties !== 'function') return;
    m.setProperties({ ...(m.properties || {}), simStatus: status == null ? null : status });
  }

  /** Clear simStatus from every node. */
  clearAllSimStatus() {
    if (!this._lf) return;
    const data = this._lf.getGraphData();
    (data.nodes || []).forEach((n) => this.setNodeSimStatus(n.id, null));
  }

  _getElk() {
    if (!this._elkPromise) {
      // Bundled build inlines the worker — no separate worker URL needed.
      // Dynamic import keeps the ~1.5 MB ELK engine out of the initial bundle.
      this._elkPromise = import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK());
    }
    return this._elkPromise;
  }

  _elkLayout(graph) {
    return this._getElk().then((elk) => elk.layout(graph));
  }

  /**
   * Auto-layout the DRD using ELK Layered. Decisions float to the top,
   * InputData sinks to the bottom, and requirement arrows point upward
   * (DMN convention: source = required element, target = dependent decision).
   * TextAnnotations and their associations are left untouched — they are
   * free-floating annotations.
   */
  async autoLayout({ hGap = 60, vGap = 80 } = {}) {
    const lf = this._lf;
    if (!lf) return;
    const { nodes, edges } = lf.getGraphData();
    if (!nodes || nodes.length === 0) return;

    const IGNORE = new Set(['dmn:TextAnnotation', 'dmn:DecisionService']);
    const flowNodes = nodes.filter((n) => !IGNORE.has(n.type));
    if (flowNodes.length === 0) return;

    const idSet = new Set(flowNodes.map((n) => n.id));
    const flowEdges = edges.filter(
      (e) => idSet.has(e.sourceNodeId) && idSet.has(e.targetNodeId)
    );

    const buildPorts = (id) => {
      const ports = [];
      if (flowEdges.some((e) => e.sourceNodeId === id))
        ports.push({ id: `${id}__out`, properties: { 'port.side': 'NORTH' } });
      if (flowEdges.some((e) => e.targetNodeId === id))
        ports.push({ id: `${id}__in`, properties: { 'port.side': 'SOUTH' } });
      return ports;
    };

    const graph = {
      id: 'root',
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': 'UP',
        'elk.separateConnectedComponents': 'false',
        'elk.layered.spacing.nodeNodeBetweenLayers': String(vGap),
        'elk.spacing.nodeNode': String(hGap),
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.cycleBreaking.strategy': 'GREEDY_MODEL_ORDER',
        'elk.layered.unnecessaryBendpoints': 'true',
        'elk.spacing.edgeNode': '20',
        'elk.padding': '[top=40, left=40, bottom=40, right=40]',
      },
      children: flowNodes.map((n) => {
        const m = lf.getNodeModelById(n.id);
        return {
          id: n.id,
          width: (m && m.width) || 140,
          height: (m && m.height) || 60,
          ports: buildPorts(n.id),
          layoutOptions: { 'elk.portConstraints': 'FIXED_SIDE' },
        };
      }),
      edges: flowEdges.map((e) => ({
        id: e.id,
        sources: [`${e.sourceNodeId}__out`],
        targets: [`${e.targetNodeId}__in`],
      })),
    };

    let laid;
    try {
      laid = await this._elkLayout(graph);
    } catch (err) {
      this._emit('layout.error', { error: err });
      throw err;
    }

    const START_X = 60;
    const START_Y = 60;

    for (const n of laid.children || []) {
      const m = lf.getNodeModelById(n.id);
      if (!m) continue;
      const w = m.width || n.width;
      const h = m.height || n.height;
      const cx = START_X + n.x + w / 2;
      const cy = START_Y + n.y + h / 2;
      const dx = cx - m.x;
      const dy = cy - m.y;
      m.x = cx;
      m.y = cy;
      if (m.text && typeof m.text === 'object') {
        m.text.x = (typeof m.text.x === 'number' ? m.text.x : cx) + dx;
        m.text.y = (typeof m.text.y === 'number' ? m.text.y : cy) + dy;
      }
    }

    const handledEdges = new Set();
    const edgeWaypoints = new Map();
    for (const e of laid.edges || []) {
      const s = e.sections && e.sections[0];
      if (!s) continue;
      const rawPts = [s.startPoint, ...(s.bendPoints || []), s.endPoint];
      if (rawPts.length < 2) continue;
      const pts = rawPts.map((p) => ({ x: START_X + p.x, y: START_Y + p.y }));
      const em = lf.getEdgeModelById(e.id);
      if (!em) continue;
      if (typeof em.setAnchors === 'function') em.setAnchors();
      em.pointsList = pts;
      em.startPoint = { ...pts[0] };
      em.endPoint = { ...pts[pts.length - 1] };
      if (typeof em.initPoints === 'function') em.initPoints();
      if (typeof em.resetTextPosition === 'function') em.resetTextPosition();
      handledEdges.add(e.id);
      edgeWaypoints.set(e.id, pts);
    }

    // Reroute edges ELK didn't touch (associations, unknown types)
    edges.forEach((e) => {
      if (handledEdges.has(e.id)) return;
      const em = lf.getEdgeModelById(e.id);
      if (!em) return;
      if (typeof em.setAnchors === 'function') em.setAnchors();
      if (typeof em.initPoints === 'function') em.initPoints();
      if (typeof em.resetTextPosition === 'function') em.resetTextPosition();
    });

    const nodesOut = flowNodes
      .map((n) => {
        const m = lf.getNodeModelById(n.id);
        return m
          ? { id: n.id, x: m.x, y: m.y, width: m.width, height: m.height }
          : null;
      })
      .filter(Boolean);
    const edgesOut = [];
    edgeWaypoints.forEach((pts, id) => {
      edgesOut.push({ id, pointsList: pts.map((p) => ({ x: p.x, y: p.y })) });
    });

    this._emit('layout.applied', { nodes: nodesOut, edges: edgesOut });

    setTimeout(() => this.fit(), 50);
  }

  resize() {
    if (typeof this._lf.resize === 'function') {
      this._lf.resize();
    }
    // The host (sfd-v2) calls resize() right after revealing the DMN overlay.
    // That's the first moment LogicFlow is guaranteed to have rendered its
    // grid SVG, so re-apply visibility here in case the construction-time
    // hide ran before .lf-grid existed.
    this._applyInitialGridVisibility();
  }

  getInstance() {
    return this._lf;
  }

  destroy() {
    if (this._gridObserver) { try { this._gridObserver.disconnect(); } catch (_) { /* noop */ } this._gridObserver = null; }
    if (this._contextPad) { this._contextPad.destroy(); this._contextPad = null; }
    if (this._containerEl) this._containerEl.innerHTML = '';
    this._lf = null;
    this._connRules = null;
    this._paletteEl = null;
    this._canvasEl = null;
  }
}
